import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/services/api/apiRequest";

export type AuthorSummary = {
  id: number | null;
  name: string;
  org: string | null;
  image_url: string | null;
};

export type ThreadSummary = {
  id: number;
  title: string;
  tags: string[];
  status: "pending" | "published" | "declined";
  reply_count: number;
  last_activity_at: string;
  author: AuthorSummary;
};

export type ThreadDetail = {
  id: number;
  title: string;
  body: string;
  tags: string[];
  status: string;
  created_at: string;
  author: AuthorSummary;
  posts: {
    id: number;
    body: string;
    status: string;
    created_at: string;
    author: AuthorSummary;
  }[];
  linked_resources: {
    id: number;
    title: string;
    category: string;
    url: string;
    access: string;
  }[];
  mentioned_links: string[];
};

export type ModerationQueue = {
  threads: {
    id: number;
    title: string;
    body: string;
    tags: string[];
    created_at: string;
    author: AuthorSummary;
  }[];
  posts: {
    id: number;
    body: string;
    thread_id: number;
    thread_title: string;
    created_at: string;
    author: AuthorSummary;
  }[];
};

export function useThreads(search: string, tag: string | null) {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  if (tag) params.set("tag", tag);
  const qs = params.toString();
  return useQuery<{ tags: string[]; threads: ThreadSummary[] }>({
    queryKey: ["afell", "threads", search, tag],
    queryFn: () => apiRequest(`/afell/threads/${qs ? `?${qs}` : ""}`),
    placeholderData: keepPreviousData,
  });
}

export function useThread(threadId: string | undefined) {
  return useQuery<ThreadDetail>({
    queryKey: ["afell", "thread", threadId],
    queryFn: () => apiRequest(`/afell/threads/${threadId}/`),
    enabled: Boolean(threadId),
  });
}

export function useCreateThread() {
  const queryClient = useQueryClient();
  return useMutation<
    { id: number; status: string },
    Error,
    { title: string; body: string; tags: string[] }
  >({
    mutationFn: (payload) => apiRequest("/afell/threads/", "POST", payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["afell", "threads"] }),
  });
}

export function useReply(threadId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation<{ id: number; status: string }, Error, string>({
    mutationFn: (body) => apiRequest(`/afell/threads/${threadId}/reply/`, "POST", { body }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["afell", "thread", threadId] }),
  });
}

export function useModerationQueue() {
  return useQuery<ModerationQueue>({
    queryKey: ["afell", "moderation"],
    queryFn: () => apiRequest("/afell/admin/moderation/"),
  });
}

export function useReviewContent() {
  const queryClient = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { kind: "threads" | "posts"; id: number; decision: "published" | "declined" }
  >({
    mutationFn: ({ kind, id, decision }) =>
      apiRequest(`/afell/admin/moderation/${kind}/${id}/`, "POST", { decision }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["afell", "moderation"] });
      queryClient.invalidateQueries({ queryKey: ["afell", "threads"] });
    },
  });
}
